/**
 * File Upload Mapping Service
 *
 * This service maintains mappings between:
 * - Original filename (what user selects)
 * - Upload ID (returned from first chunk)
 * - Server filename (returned from final response with hash suffix)
 * - Dataset ID (for tracking purposes)
 *
 * This solves duplicate file issues by providing definitive mapping
 * instead of relying on complex filename matching strategies.
 */

export interface FileUploadMapping {
  originalFilename: string;
  uploadId: string;
  serverFilename?: string;
  datasetId?: number;
  dialogueId?: number;
  timestamp: number;
  context?: string; // 'dialogue', 'project', etc.
}

class FileUploadMappingService {
  private mappings: Map<string, FileUploadMapping> = new Map();
  private uploadIdIndex: Map<string, string> = new Map(); // uploadId -> originalFilename
  private serverFilenameIndex: Map<string, string> = new Map(); // serverFilename -> originalFilename

  /**
   * Add initial mapping when upload starts (after first chunk returns upload_id)
   */
  addMapping(
    originalFilename: string,
    uploadId: string,
    dialogueId?: number,
    context?: string,
  ): void {
    const mapping: FileUploadMapping = {
      originalFilename,
      uploadId,
      timestamp: Date.now(),
      dialogueId,
      context,
    };

    this.mappings.set(originalFilename, mapping);
    this.uploadIdIndex.set(uploadId, originalFilename);
  }

  /**
   * Update mapping with server response (final chunk completion)
   */
  updateServerFilename(
    uploadId: string,
    serverFilename: string,
    datasetId: number,
  ): FileUploadMapping | null {
    const originalFilename = this.uploadIdIndex.get(uploadId);
    if (!originalFilename) {
      console.warn(`⚠️ No mapping found for upload_id: ${uploadId}`);
      return null;
    }

    const mapping = this.mappings.get(originalFilename);
    if (!mapping) {
      console.warn(`⚠️ No mapping found for filename: ${originalFilename}`);
      return null;
    }

    // Update the mapping
    mapping.serverFilename = serverFilename;
    mapping.datasetId = datasetId;

    // Update indices
    this.serverFilenameIndex.set(serverFilename, originalFilename);

    return mapping;
  }

  /**
   * Get mapping by original filename
   */
  getMappingByFilename(filename: string): FileUploadMapping | null {
    return this.mappings.get(filename) || null;
  }

  /**
   * Get mapping by upload ID
   */
  getMappingByUploadId(uploadId: string): FileUploadMapping | null {
    const originalFilename = this.uploadIdIndex.get(uploadId);
    if (!originalFilename) return null;
    return this.mappings.get(originalFilename) || null;
  }

  /**
   * Get mapping by server filename
   */
  getMappingByServerFilename(serverFilename: string): FileUploadMapping | null {
    const originalFilename = this.serverFilenameIndex.get(serverFilename);
    if (!originalFilename) return null;
    return this.mappings.get(originalFilename) || null;
  }

  /**
   * Find dataset ID for a given original filename
   */
  getDatasetIdByFilename(filename: string): number | null {
    const mapping = this.mappings.get(filename);
    return mapping?.datasetId || null;
  }

  /**
   * Find server filename for a given original filename
   */
  getServerFilenameByOriginal(originalFilename: string): string | null {
    const mapping = this.mappings.get(originalFilename);
    return mapping?.serverFilename || null;
  }

  /**
   * Check if a file has been fully processed (has server filename and dataset ID)
   */
  isFileFullyMapped(filename: string): boolean {
    const mapping = this.mappings.get(filename);
    return !!(mapping?.serverFilename && mapping?.datasetId);
  }

  /**
   * Remove mapping (cleanup after file processing complete)
   */
  removeMapping(filename: string): void {
    const mapping = this.mappings.get(filename);
    if (mapping) {
      this.uploadIdIndex.delete(mapping.uploadId);
      if (mapping.serverFilename) {
        this.serverFilenameIndex.delete(mapping.serverFilename);
      }
      this.mappings.delete(filename);
    }
  }

  /**
   * Clear all mappings (for cleanup)
   */
  clearAllMappings(): void {
    this.mappings.clear();
    this.uploadIdIndex.clear();
    this.serverFilenameIndex.clear();
  }

  /**
   * Get all current mappings (for debugging)
   */
  getAllMappings(): FileUploadMapping[] {
    return Array.from(this.mappings.values());
  }

  /**
   * Clean up old mappings (older than specified time)
   */
  cleanupOldMappings(maxAgeMs: number = 24 * 60 * 60 * 1000): void {
    const cutoffTime = Date.now() - maxAgeMs;
    const toRemove: string[] = [];

    for (const [filename, mapping] of this.mappings) {
      if (mapping.timestamp < cutoffTime) {
        toRemove.push(filename);
      }
    }

    toRemove.forEach((filename) => this.removeMapping(filename));
  }

  /**
   * Find matching dataset from upload result using mapping
   */
  findDatasetFromUploadResult(
    originalFilename: string,
    uploadResult: any,
  ): { datasetId: number; serverFilename: string } | null {
    // Get the mapping for this file
    const mapping = this.getMappingByFilename(originalFilename);
    if (!mapping) {
      return null;
    }

    // Extract datasets from the upload result
    let datasets: any[] = [];

    if (Array.isArray(uploadResult)) {
      datasets = uploadResult;
    } else if (uploadResult.dataset_folder?.datasets?.length > 0) {
      datasets = uploadResult.dataset_folder.datasets;
    } else if (uploadResult.id && uploadResult.name) {
      datasets = [uploadResult];
    }

    if (datasets.length === 0) {
      return null;
    }

    // Strategy 1: Use upload_id matching if available in dataset metadata
    if (mapping.uploadId) {
      const uploadIdMatch = datasets.find(
        (d) => d.upload_id === mapping.uploadId,
      );
      if (uploadIdMatch) {
        return {
          datasetId: uploadIdMatch.id,
          serverFilename: uploadIdMatch.name,
        };
      }
    }

    // Strategy 2: Exact filename match
    const exactMatch = datasets.find((d) => d.name === originalFilename);
    if (exactMatch) {
      return {
        datasetId: exactMatch.id,
        serverFilename: exactMatch.name,
      };
    }

    // Strategy 3: Pattern matching (filename with hash suffix)
    const nameWithoutExt = originalFilename.substring(
      0,
      originalFilename.lastIndexOf("."),
    );
    const ext = originalFilename.substring(originalFilename.lastIndexOf("."));

    const patternMatch = datasets.find((d) => {
      return d.name.startsWith(nameWithoutExt) && d.name.endsWith(ext);
    });

    if (patternMatch) {
      return {
        datasetId: patternMatch.id,
        serverFilename: patternMatch.name,
      };
    }

    // Strategy 4: Single dataset fallback
    if (datasets.length === 1) {
      return {
        datasetId: datasets[0].id,
        serverFilename: datasets[0].name,
      };
    }

    return null;
  }
}

// Create singleton instance
export const fileUploadMappingService = new FileUploadMappingService();

// Export the class for testing purposes
export { FileUploadMappingService };
